/**
 * lobby/socket.ts —— 大廳的 socket.io client。
 *
 * 照原版 sourcemap（reference/lobby/src/stores/useSocket.ts、typings/socket.ts）：
 *   lobbyInitial({token, clientType}) → {status, message, token(新的), content:{games, user}}
 *   lobbyPlay({token, clientType, code}) → {status, message, token, redirectUrl}
 *   server 推 alert({status, message})
 *
 * token 一次性：每次 ack 都換新的，要寫回網址列（原版也是這樣做，重整才不會用到失效的 token）。
 */

import { io, Socket } from 'socket.io-client';
import type { GameType, UserType } from './main';

export interface LobbyContent { games: GameType[]; user: UserType }

interface LobbyInitialRes { status: number; message: string; token?: string; content?: LobbyContent }
interface LobbyPlayRes { status: number; message: string; token?: string; redirectUrl?: string }

export class LobbySocket {
    private socket: Socket;
    private token: string;
    private clientType: string;

    constructor(params: URLSearchParams) {
        this.token = params.get('t') ?? '';
        this.clientType = params.get('client_type') ?? 'web';
        const raw = params.get('socket_url');
        const base = raw
            ? (/^https?:\/\//.test(raw) ? raw : `${location.protocol}//${raw}`)
            : location.origin;
        this.socket = io(`${base}/lobby`, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            forceNew: true,
        });
    }

    onAlert(cb: (msg: string) => void): void {
        this.socket.on('alert', (res: { status: number; message: string }) => cb(res.message));
    }

    /** 換掉網址列上的 token（不重新整理），跟原版一樣 */
    private keepToken(token?: string): void {
        if (!token) return;
        this.token = token;
        const url = new URL(location.href);
        url.searchParams.set('t', token);
        history.replaceState(null, '', url.toString());
    }

    private emit<T>(event: string, payload: Record<string, unknown>, timeoutMs = 10000): Promise<T> {
        return new Promise((resolve, reject) => {
            let done = false;
            const timer = setTimeout(() => { if (!done) { done = true; reject(new Error(`${event} timeout`)); } }, timeoutMs);
            this.socket.emit(event, { ...payload, token: this.token, clientType: this.clientType }, (res: T) => {
                if (done) return;
                done = true;
                clearTimeout(timer);
                resolve(res);
            });
        });
    }

    async waitConnected(timeoutMs = 8000): Promise<void> {
        if (this.socket.connected) return;
        await new Promise<void>((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('socket connect timeout')), timeoutMs);
            this.socket.once('connect', () => { clearTimeout(timer); resolve(); });
            this.socket.once('connect_error', (e) => { clearTimeout(timer); reject(e); });
        });
    }

    async initial(): Promise<LobbyContent> {
        await this.waitConnected();
        const res = await this.emit<LobbyInitialRes>('lobbyInitial', {});
        this.keepToken(res.token);
        if (res.status !== 200 || !res.content) throw new Error(res.message || 'lobbyInitial failed');
        return res.content;
    }

    async play(code: string): Promise<string> {
        const res = await this.emit<LobbyPlayRes>('lobbyPlay', { code });
        this.keepToken(res.token);
        if (res.status !== 200 || !res.redirectUrl) throw new Error(res.message || 'lobbyPlay failed');
        return res.redirectUrl;
    }
}
